/**
 * Tells the page that the data of the signed-in user changed on the server, e.g. after clocking in on the phone, so
 * polling queries fetch right away instead of with their next poll. Polling stays, in case an update gets lost.
 *
 * It reads the server-sent events with fetch, since EventSource cannot send the access token. It is connected only
 * while someone listens and the page is visible and online, since a page fetches anyway once it is back.
 *
 * A connection can die without the browser noticing, e.g. while the device slept. Since the server sends a heartbeat
 * every 30 seconds, a stream silent for longer than [STALE_AFTER] is given up and connected again, and the listeners
 * are told once it is back, since changes may have been missed meanwhile.
 */

// How long it waits before connecting again, growing with every failed attempt
const RETRY_DELAYS = [1000, 5000, 15000, 60000]
// Clocking out changes several things at once, and some of them are updated by the server a moment later
const NOTIFY_DELAY = 1000
// More than two heartbeats of the server
const STALE_AFTER = 75000

function createLiveUpdates ({
  url,
  headers,
  refreshAccess,
  fetch = (...args) => window.fetch(...args),
  staleAfter = STALE_AFTER
}) {
  const listeners = new Set()
  let controller = null
  let retryTimeout = null
  let notifyTimeout = null
  let failures = 0
  // Whether a stream was lost, so changes may have been missed until the next one opens
  let lost = false

  function shouldConnect () {
    return listeners.size > 0 && document.visibilityState === 'visible' && navigator.onLine
  }

  function update () {
    if (shouldConnect()) {
      connect()
    } else {
      disconnect()
    }
  }

  async function connect () {
    if (controller != null || retryTimeout != null) return
    const current = new AbortController()
    controller = current
    let opened = false
    let watchdog = null
    let stale = false
    const watch = () => {
      clearTimeout(watchdog)
      watchdog = setTimeout(() => {
        stale = true
        current.abort()
      }, staleAfter)
    }
    try {
      watch()
      const response = await fetch(url, {
        headers: { Accept: 'text/event-stream', ...await headers() },
        credentials: 'include',
        signal: current.signal
      })
      // The access token may have expired while the device was asleep, so the next attempt gets a new one
      if (response.status === 401 && refreshAccess) await refreshAccess().catch(() => {})
      if (!response.ok) throw new Error(`Live updates answered with ${response.status}`)
      opened = true
      failures = 0
      watch()
      if (lost) {
        lost = false
        notify()
      }
      await read(response.body, watch)
    } catch (error) {
      if (!opened && (stale || !current.signal.aborted)) failures++
    }
    clearTimeout(watchdog)
    // Disconnected meanwhile
    if (controller !== current) return
    controller = null
    if (opened) lost = true
    // The server ends the stream after a while, so the access token is checked again
    const delay = opened ? RETRY_DELAYS[0] : RETRY_DELAYS[Math.min(failures, RETRY_DELAYS.length) - 1]
    retryTimeout = setTimeout(() => {
      retryTimeout = null
      update()
    }, delay)
  }

  // A page that is hidden or offline fetches anyway once it is back
  function disconnect () {
    clearTimeout(retryTimeout)
    retryTimeout = null
    lost = false
    if (controller != null) {
      const current = controller
      controller = null
      current.abort()
    }
  }

  async function read (body, onData) {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    for (;;) {
      const { done, value } = await reader.read()
      if (done) return
      onData()
      buffer += decoder.decode(value, { stream: true })
      const events = buffer.split(/\r?\n\r?\n/)
      buffer = events.pop()
      events.forEach(handleEvent)
    }
  }

  function handleEvent (event) {
    const name = event.split(/\r?\n/).find(line => line.startsWith('event:'))
    if (name != null && name.slice('event:'.length).trim() === 'changed') notify()
  }

  function notify () {
    clearTimeout(notifyTimeout)
    notifyTimeout = setTimeout(() => listeners.forEach(listener => listener()), NOTIFY_DELAY)
  }

  /**
   * Calls [listener] whenever the data changed, until the returned function is called.
   */
  function listen (listener) {
    listeners.add(listener)
    if (listeners.size === 1) {
      document.addEventListener('visibilitychange', update)
      window.addEventListener('online', update)
      window.addEventListener('offline', update)
    }
    update()
    return () => {
      if (!listeners.delete(listener)) return
      if (listeners.size === 0) {
        document.removeEventListener('visibilitychange', update)
        window.removeEventListener('online', update)
        window.removeEventListener('offline', update)
        clearTimeout(notifyTimeout)
      }
      update()
    }
  }

  return { listen }
}

let liveUpdates = null

// Without options, e.g. at the end of a test, nothing is told anymore
function configureLiveUpdates (options) {
  liveUpdates = options == null ? null : createLiveUpdates(options)
}

function listenToLiveUpdates (listener) {
  return liveUpdates == null ? () => {} : liveUpdates.listen(listener)
}

export { createLiveUpdates, configureLiveUpdates, listenToLiveUpdates }
